import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/context/ThemeContext";
import { 
  Users, 
  Search, 
  Plus, 
  Trophy,
  MapPin,
  Star,
  Shield,
  Crown,
  Coins,
  Filter
} from "lucide-react";

const LEVEL_COLORS = {
  1: "bg-amber-700",      // Bronz
  2: "bg-gray-400",       // Gümüş
  3: "bg-yellow-500",     // Altın
  4: "bg-cyan-400",       // Platin
  5: "bg-purple-500",     // Elmas
};

const LEVEL_NAMES = {
  1: "Bronz",
  2: "Gümüş",
  3: "Altın",
  4: "Platin",
  5: "Elmas"
};

const DEFAULT_LOGOS = {
  // Kuşlar
  eagle: "🦅",
  canary: "🐤",
  falcon: "🦅",
  owl: "🦉",
  phoenix: "🔥",
  // Kedigiller
  lion: "🦁",
  tiger: "🐯",
  panther: "🐆",
  leopard: "🐆",
  // Diğer Hayvanlar
  wolf: "🐺",
  bear: "🐻",
  bull: "🐂",
  horse: "🐴",
  crocodile: "🐊",
  shark: "🦈",
  dragon: "🐉",
  snake: "🐍",
  scorpion: "🦂",
  bee: "🐝",
  // Semboller
  lightning: "⚡",
  star: "⭐",
  fire: "🔥",
  trophy: "🏆",
  shield: "🛡️",
  sword: "⚔️",
  crown: "👑",
  diamond: "💎",
  rocket: "🚀",
  anchor: "⚓",
};

const ClubsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [clubs, setClubs] = useState([]);
  const [myClub, setMyClub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterRecruiting, setFilterRecruiting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clubsRes, myClubRes] = await Promise.all([
        axios.get(`${API}/clubs`),
        axios.get(`${API}/my-club`).catch(() => ({ data: null }))
      ]);
      
      setClubs(clubsRes.data);
      setMyClub(myClubRes.data);
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredClubs = clubs.filter(club => {
    const matchesSearch = club.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         club.city?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRecruiting = !filterRecruiting || club.isRecruiting;
    return matchesSearch && matchesRecruiting;
  });

  const getLogo = (club) => {
    if (club.logoType === "custom" && club.logo) {
      return <img src={club.logo} alt={club.name} className="w-full h-full object-cover rounded-xl" />;
    }
    return <span className="text-4xl">{DEFAULT_LOGOS[club.logo] || "🏟️"}</span>;
  };

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-[#1f2224]' : 'bg-gradient-to-br from-slate-50 via-white to-emerald-50'}`}>
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDark ? 'bg-[#1f2224]' : 'bg-gradient-to-br from-slate-50 via-white to-emerald-50'}`}>
      <Navbar user={user} onLogout={onLogout} />
      
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Shield className="h-8 w-8" />
                Kulüpler
              </h1>
              <p className="text-emerald-100 mt-1">
                Kulüplere katıl veya kendi kulübünü kur
              </p>
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline"
                className="bg-white/20 border-white/30 text-white hover:bg-white/30"
                onClick={() => navigate("/credits")}
              >
                <Coins className="h-4 w-4 mr-2" />
                Kredi Al
              </Button>
              
              {!myClub && (
                <Button 
                  className="bg-white text-emerald-600 hover:bg-emerald-50"
                  onClick={() => navigate("/clubs/create")}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Kulüp Kur
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* My Club Card */}
        {myClub && (
          <Card className={`mb-8 ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : 'bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200'}`}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-emerald-400' : 'text-emerald-800'}`}>
                  <Crown className="h-5 w-5" />
                  Kulübüm
                </CardTitle>
                <Button 
                  size="sm"
                  onClick={() => navigate(`/clubs/${myClub.id}`)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  Kulübüme Git
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: myClub.primaryColor + '20' }}
                >
                  {getLogo(myClub)}
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${isDark ? 'text-white' : 'text-gray-900'}`}>{myClub.name}</h3>
                  <div className="flex items-center gap-3 mt-1">
                    <Badge className={`${LEVEL_COLORS[myClub.level]} text-white`}>
                      {LEVEL_NAMES[myClub.level]}
                    </Badge>
                    <span className={`text-sm flex items-center gap-1 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                      <Users className="h-3 w-3" />
                      {myClub.members?.length || 0}/{myClub.memberLimit}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Kulüp veya şehir ara..."
              className={`pl-10 ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40] text-white' : ''}`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <Button
            variant={filterRecruiting ? "default" : "outline"}
            onClick={() => setFilterRecruiting(!filterRecruiting)}
            className={filterRecruiting ? "bg-emerald-600 hover:bg-emerald-700" : isDark ? 'border-[#3a3d40] text-white hover:bg-[#2a2d2f]' : ''}
          >
            <Filter className="h-4 w-4 mr-2" />
            Üye Alan
          </Button>
        </div>

        {/* Clubs Grid */}
        {filteredClubs.length === 0 ? (
          <Card className={`text-center py-12 ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}>
            <Shield className={`h-16 w-16 mx-auto mb-4 ${isDark ? 'text-gray-500' : 'text-gray-300'}`} />
            <h3 className={`text-lg font-medium ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Kulüp bulunamadı</h3>
            <p className={`mt-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>İlk kulübü kuran siz olun!</p>
            {!myClub && (
              <Button 
                className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate("/clubs/create")}
              >
                <Plus className="h-4 w-4 mr-2" />
                Kulüp Kur
              </Button>
            )}
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredClubs.map((club) => (
              <Card 
                key={club.id}
                className={`hover:shadow-lg transition-all cursor-pointer group ${isDark ? 'bg-[#2a2d2f] border-[#3a3d40]' : ''}`}
                onClick={() => navigate(`/clubs/${club.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div 
                      className="w-16 h-16 rounded-xl flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform"
                      style={{ backgroundColor: club.primaryColor + '20' }}
                    >
                      {getLogo(club)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h3 className={`font-bold text-lg truncate ${isDark ? 'text-white' : ''}`}>{club.name}</h3>
                        <Badge className={`${LEVEL_COLORS[club.level]} text-white shrink-0`}>
                          {LEVEL_NAMES[club.level]}
                        </Badge>
                      </div>
                      
                      {club.description && (
                        <p className={`text-sm mt-1 line-clamp-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                          {club.description}
                        </p>
                      )}
                      
                      <div className={`flex items-center gap-4 mt-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {club.members?.length || 0}/{club.memberLimit}
                        </span>
                        
                        {club.city && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {club.city}
                          </span>
                        )}
                        
                        <span className="flex items-center gap-1">
                          <Trophy className="h-3 w-3" />
                          {club.stats?.wins || 0}G
                        </span>
                      </div>
                      
                      {club.isRecruiting && (
                        <Badge variant="outline" className="mt-3 border-emerald-500 text-emerald-600">
                          <Plus className="h-3 w-3 mr-1" />
                          Üye Alıyor
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClubsPage;
